import { keepPreviousData, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useMemo } from "react";

import {
  IAuditLog,
  IAuditLogFilterOptions,
  IAuditLogFilters,
  IEntityAuditLogFilters,
} from "../../../@types/AuditLog";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import {
  auditLogService,
  AUDIT_LOG_CONDITION_TABLE,
  AUDIT_LOG_MAX_ROWS,
} from "../../../services/admin/auditLogService";
import { ensureOk } from "../../../utils/ensureOk";

type AuditLogsPaginatedResponse = IPaginatedResponse<IAuditLog[]>;

/**
 * Default window of the entity history panels: the last 90 days, counted as
 * calendar days including today — the widest window the API accepts (EN-5288).
 */
export const AUDIT_LOG_WINDOW_DAYS = 90;

/**
 * Turns a paged audit response into the `TablePagination` state. Above
 * `AUDIT_LOG_MAX_ROWS` the API stopped counting, so the total reads "10000+"
 * and paging stops at that many rows (the API refuses a page past them).
 */
export function toAuditPagination(
  data: AuditLogsPaginatedResponse | undefined,
  page: number,
  pageSize: number
) {
  const currentPage = data?.currentPage ?? page;
  const effectivePageSize = data?.pageSize ?? pageSize;
  const totalCount = data?.totalCount ?? 0;
  const capped = totalCount > AUDIT_LOG_MAX_ROWS;
  const totalPages = capped
    ? Math.floor(AUDIT_LOG_MAX_ROWS / effectivePageSize)
    : Math.max(data?.pages ?? 1, 1);

  return {
    currentPage,
    pageSize: effectivePageSize,
    totalPages,
    totalItems: capped ? AUDIT_LOG_MAX_ROWS : totalCount,
    totalIsLowerBound: capped,
    hasNextPage: capped ? currentPage < totalPages : data?.hasNextPage ?? false,
    hasPreviousPage: data?.hasPreviousPage ?? false,
  };
}

/**
 * Paginated, fully-filterable audit log query behind the standalone Audit
 * Logs page (`/admin/settings/audit-logs`, EN-4985). The API paginates
 * server-side here, so `filters.page`/`pageSize` are part of the query key.
 */
export function useAuditLogs(
  filters: IAuditLogFilters,
  enabled: boolean = true
) {
  const { data, isLoading, isError, isFetching, error, refetch } =
    useQuery<AuditLogsPaginatedResponse>({
      queryKey: [
        "admin-audit-logs",
        filters.page,
        filters.pageSize,
        filters.schemaName,
        filters.tableName,
        filters.operation,
        filters.operations,
        filters.source,
        filters.applicationUserId,
        filters.applicationUserEmail,
        filters.entityId,
        filters.startDate,
        filters.endDate,
      ],
      queryFn: async () =>
        ensureOk(await auditLogService.getAuditLogs(filters)),
      enabled,
    });

  return {
    auditLogs: data?.result ?? [],
    pagination: toAuditPagination(data, filters.page, filters.pageSize),
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Audit trail for a single entity, feeding the shared `AuditLogPanel`
 * (EN-5147), one server-side page of one window at a time (EN-5288).
 * `cms_condition` is routed to the patient-condition endpoint instead, which
 * takes the same window and filters, and the query stays
 * disabled until an entity id is known (Retool's
 * `queryDisabled: {{ !entityId.value }}`).
 */
export function useEntityAuditLogs(
  filters: IEntityAuditLogFilters,
  enabled: boolean = true
) {
  const {
    tableName,
    entityId,
    page,
    pageSize,
    operations,
    source,
    applicationUserEmail,
  } = filters;
  const isConditionTable = tableName === AUDIT_LOG_CONDITION_TABLE;

  // Day-granularity defaults: a raw `dayjs()` would change every render and
  // re-key the query on each one.
  const startDate = useMemo(
    () =>
      filters.startDate ??
      dayjs()
        .subtract(AUDIT_LOG_WINDOW_DAYS - 1, "day")
        .startOf("day")
        .toISOString(),
    [filters.startDate]
  );
  const endDate = useMemo(
    () => filters.endDate ?? dayjs().endOf("day").toISOString(),
    [filters.endDate]
  );

  const { data, isLoading, isError, isFetching, error, refetch } =
    useQuery<AuditLogsPaginatedResponse>({
      queryKey: [
        "admin-entity-audit-logs",
        tableName,
        entityId,
        startDate,
        endDate,
        operations,
        source,
        applicationUserEmail,
        page,
        pageSize,
      ],
      queryFn: async () => {
        const query = {
          startDate,
          endDate,
          operations,
          source,
          applicationUserEmail,
          page,
          pageSize,
        };
        return ensureOk(
          isConditionTable
            ? await auditLogService.getPatientConditionAuditLogs(
                entityId,
                query
              )
            : await auditLogService.getEntityAuditLogs({
                ...query,
                tableName,
                entityId,
              })
        );
      },
      enabled: enabled && Boolean(entityId) && Boolean(tableName),
      placeholderData: keepPreviousData,
      // The global QueryClient disables refetchOnMount with a 3-minute
      // staleTime (config/query.ts) and migrated admin surfaces carry no
      // manual Refresh button (EN-5129 UI feedback), so re-opening a host
      // page must re-read the trail rather than show a stale one.
      refetchOnMount: "always",
    });

  return {
    auditLogs: data?.result ?? [],
    pagination: toAuditPagination(data, page, pageSize),
    isLoading,
    isError,
    isFetching,
    error,
    refetch,
  };
}

/**
 * One audit row with its before/after snapshots, for the detail modal: the
 * lists leave them out to stay small (EN-5288).
 */
export function useAuditLogDetail(id: string, enabled: boolean = true) {
  const { data, isLoading, isError, error } = useQuery<IAuditLog>({
    queryKey: ["admin-audit-log", id],
    queryFn: async () => ensureOk(await auditLogService.getAuditLog(id)),
    enabled: enabled && Boolean(id),
  });

  return { auditLog: data, isLoading, isError, error };
}

/** Dropdown choices for the Audit Logs page. The API caches them for an hour. */
export function useAuditLogFilterOptions() {
  const { data, isLoading } = useQuery<IAuditLogFilterOptions>({
    queryKey: ["admin-audit-log-filter-options"],
    queryFn: async () => ensureOk(await auditLogService.getFilterOptions()),
    staleTime: 60 * 60 * 1000,
  });

  return { filterOptions: data, isLoading };
}
